import { Injectable, inject } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { BasketItem } from '@df/session/api/basket/basket-item';
import defaultsDeep from '@df/util/fn/defaults-deep';
import { Nexus } from '@tomandco/nexus-api-types';
import { BehaviorSubject, Subject } from 'rxjs';
import { IProductConfigurationOptions, ProductConfiguration } from '../product-configuration';
import { BundleOption } from './options/option/bundle-option';
import { ProductConfigurationBundleOptionService } from './options/product-configuration-bundle-option.service';
@Injectable()
export class ProductConfigurationBundle extends ProductConfiguration {
    /**
     * Configuration type
     */
    readonly type = 'bundle';

    /**
     * Bundle options mutation
     */
    readonly bundleOptions$ = new BehaviorSubject<BundleOption[] | undefined>(undefined);

    /**
     * Bundle options data mutation
     */
    readonly bundleOptionsDataChange$ = new Subject<BundleOption>();

    /**
     * Bundle product data service
     */
    private optionService = inject(ProductConfigurationBundleOptionService);
    /**
     * Module config
     */
    private moduleConfig = inject(NProductConfiguration.OPTIONS);

    /**
     * Configuration
     */
    protected config?: NProductConfiguration.Bundle.Config;

    override init(
        product: Product<IProductDataV3>,
        options: IProductConfigurationOptions,
        config?: Partial<NProductConfiguration.Bundle.Config>
    ) {
        this.config = defaultsDeep(config || {}, this.moduleConfig.bundle) as NProductConfiguration.Bundle.Config;
        super.init(product, options);

        // sync options with product
        this.subscriptions.add(
            this.product$.subscribe(product => {
                if (product) {
                    const options = this.optionService.getOptions(product, this);
                    if (this.bundleOptions$.value !== options) {
                        this.bundleOptions$.next(options);
                    }
                }
            })
        );

        // notify about a change when bundle options are modified
        this.subscriptions.add(
            this.bundleOptionsDataChange$.subscribe(() => {
                this.priceChange$.next();
                this.productRequestChange$.next();
            })
        );
    }

    override validate(): NProductConfiguration.Error.Entry[] | undefined {
        const errors = [...(this.optionService.validate(this.bundleOptions$.value) || []), ...(super.validate() || [])];
        return errors.length ? errors : undefined;
    }

    override isFromPrice(): boolean {
        return false;
    }

    // TODO: write proper logic
    //  override getUrl(): string | undefined {
    //     const path = this.product$.value?.url;
    //     if(!path) {
    //         return undefined;
    //     }
    //     if(this.config?.storageKey === false) {
    //         return path;
    //     }
    // }

    /**
     * Basket request representations of the current selection
     */
    override toBasketRequest(): Nexus.Lib.Basket.BasketRequestData {
        const request = super.toBasketRequest();
        request.bundle_options = this.toBundleSelectionData();
        return request;
    }

    override getSelectedProducts(): { product: Product<IProductDataV3>; qty: number }[] {
        const selections: { product: Product<IProductDataV3>; qty: number }[] = [];
        this.bundleOptions$.value?.forEach(option => {
            const value = option.selectedValue;
            if (value) {
                selections.push({
                    product: value.product as Product<IProductDataV3>,
                    qty: value.qty
                });
            }
        });
        return selections;
    }

    override byItem(item: BasketItem): void {
        super.byItem(item);
        this.optionService.setSelection(this.bundleOptions$.value, item.data.request.bundle_options!);
    }

    /**
     * Retrieves bundle selection data
     */
    private toBundleSelectionData(): Nexus.Lib.Basket.BundleSelectionData | undefined {
        return this.optionService.getRequestOptions(this.bundleOptions$.value);
    }
}
